import React from "react";

import { Icon } from "@/components/Icon";
import { QuantityWithUOMAbbr } from "@/components/QuantityWithUOMAbbr";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { ExistingInventorySource } from "../../queries/tracking-data/existing-inventory";
import { Item } from "../../queries/tracking-data/items";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";

export interface ExistingInventorySourceListProps {
  item: Item;
  sources: ExistingInventorySource[];
  fromRowId?: string;
}

export const ExistingInventorySourceList: React.FC<ExistingInventorySourceListProps> = ({
  item,
  sources,
  fromRowId,
}) => {
  return (
    <ul className="mb-0 list-unstyled">
      {sources.map((source, index) => (
        <li key={index}>
          <ExistingInventorySourceLine fromRowId={fromRowId} item={item} source={source} />
        </li>
      ))}
    </ul>
  );
};

const ExistingInventorySourceLine: React.FC<{
  fromRowId?: string;
  item: Item;
  source: ExistingInventorySource;
}> = ({ fromRowId, item, source }) => {
  const { type, balance, sourceRecord, sourceLine } = source;
  const bin =
    "bin" in sourceLine ? sourceLine.bin
    : balance > 0 ? sourceLine.fromBin
    : sourceLine.toBin;
  const binLabel =
    type === "Shipment" && balance < 0 ? `${sourceLine.job?.name ?? "unknown"} shipment`
    : bin?.number ? <>&apos;{bin.number}&apos; on</>
    : item.type === "Non-inventory Item" ? "non-inventory stock on"
    : <>&apos;unknown bin&apos; on</>;
  const buildInputOutputLabel =
    type === "Build Input" ? "input "
    : type === "Build Output" ? "output "
    : null;
  return (
    <div className="d-flex">
      <div className="me-1">
        {balance > 0 ?
          <Icon icon="plus-lg" className="text-success" />
        : <Icon icon="dash-lg" className="text-danger" />}
      </div>
      <div>
        <QuantityWithUOMAbbr quantity={Math.abs(balance)} uom={item.baseItemUOM?.uom} /> {balance > 0 ? "from" : "to"}{" "}
        {binLabel} <ExistingInventorySourceLink fromRowId={fromRowId} source={source} /> {buildInputOutputLabel}line #
        {sourceLine.lineNumber.toFixed()}
        {sourceRecord.comment && (
          <>
            <br />
            <span className="text-secondary">{sourceRecord.comment}</span>
          </>
        )}
      </div>
    </div>
  );
};

const ExistingInventorySourceLink: React.FC<{ fromRowId?: string; source: ExistingInventorySource }> = ({
  fromRowId,
  source: { type, balance, sourceRecordType, sourceRecord, sourceLine },
}) => {
  // Do not link anywhere for adjustments or transfers, we don't have a dedicated page for them
  if (type === "Adjustment" || type === "Transfer")
    return <SosEntityLink type={sourceRecordType} id={sourceRecord.id} label={sourceRecord.number} />;

  // Do not link anywhere if the record is not part of this sales order (i.e. a shipment from a different sales order)
  // In this scenario, the source record is not included in any tracking tables for this sales order
  if ((type === "Shipment" || type === "Build Input") && balance < 0)
    return <SosEntityLink type={sourceRecordType} id={sourceRecord.id} label={sourceRecord.number} />;

  return (
    <span className="me-1">
      <JobTrackingInternalLink trackerPath={type === "Shipment" ? "shipments" : "builds"}>
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityLineItemHashLink
            fromHash={fromRowId}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type={sourceRecordType}
            id={sourceRecord.id}
            lineNumber={sourceLine.lineNumber}
            label={sourceRecord.number}
          />
        )}
      </JobTrackingInternalLink>
    </span>
  );
};
